// The simulated assessment run.
//
// No model is called. The run walks the project's students in roster order
// and, for each one, narrates what the real pipeline does (read the files,
// score the descriptors) on a timer, then reveals that student's REAL grades
// from the fixtures by adding them to the project's demo state. Pages read
// the state through the hooks in ./hooks.ts, so the disc, the matrix and the
// student pages fill in as each student completes.
//
// Progress lines live in memory only: navigating away and back keeps them,
// a full reload drops the log but `resumeIfRunning` finishes any run that
// was mid-flight (the state in localStorage says which students are done).
import { projectFixtures } from './fixtures'
import { getProjectDemoState, revealStudent, setProjectDemoState } from './demoState'

export interface ProgressLine {
  id: number
  text: string
  kind: 'step' | 'file' | 'done'
}

/**
 * The stages the real pipeline goes through for one student, in the words the
 * panel shows: read the files → work out what was made → find the skill
 * developmental descriptors the work gives evidence for → score them.
 */
export type Phase = 'reading' | 'understanding' | 'matching' | 'scoring' | 'done'

export interface StageFile {
  name: string
  kind: string
}

/** What the panel shows for the student being assessed right now. */
export interface StageStatus {
  studentId: number
  studentName: string
  phase: Phase
  files: StageFile[]
  /** index into `files` while reading; null otherwise */
  activeFile: number | null
  /** descriptors this student's work is scored on (from the real run) */
  descriptors: number
}

export interface StudentProgress {
  studentId: number
  studentName: string
  state: 'waiting' | 'active' | 'done'
}

export interface ProgressSnapshot {
  lines: ProgressLine[]
  /** 0..1 — fraction of the timeline played */
  fraction: number
  /** students fully revealed / total */
  done: number
  total: number
  currentStudent: string | null
  stage: StageStatus | null
  students: StudentProgress[]
  running: boolean
}

type Step = {
  delay: number
  line?: Omit<ProgressLine, 'id'>
  reveal?: number
  student?: string | null
  stage?: StageStatus | null
}

const MAX_FILE_LINES = 6
const TIMING = {
  start: 900,
  read: 1100,
  perFile: 650,
  understand: 1600,
  match: 1500,
  scoreBase: 2200,
  perDescriptor: 110,
  scoreCap: 4500,
  afterReveal: 900,
}

type Run = {
  timer: number | null
  steps: Step[]
  index: number
  lines: ProgressLine[]
  current: string | null
  stage: StageStatus | null
  order: number[]
}

const runs = new Map<string, Run>()
const listeners = new Map<string, Set<() => void>>()
let lineSeq = 1

function notify(projectId: string) {
  listeners.get(projectId)?.forEach(cb => cb())
}

export function subscribeProgress(projectId: string, cb: () => void): () => void {
  if (!listeners.has(projectId)) listeners.set(projectId, new Set())
  const set = listeners.get(projectId) as Set<() => void>
  set.add(cb)
  return () => {
    set.delete(cb)
  }
}

const EMPTY_SNAPSHOT: ProgressSnapshot = {
  lines: [],
  fraction: 0,
  done: 0,
  total: 0,
  currentStudent: null,
  stage: null,
  students: [],
  running: false,
}

function studentsProgress(projectId: string, revealed: number[], current: number | null, order?: number[]): StudentProgress[] {
  const fixture = projectFixtures[projectId]
  const roster = fixture?.dashboard.students ?? []
  const byOrder = order ? [...roster].sort((a, b) => order.indexOf(a.student_id) - order.indexOf(b.student_id)) : roster
  return byOrder.map(s => ({
    studentId: s.student_id,
    studentName: s.student_name,
    state: revealed.includes(s.student_id) ? 'done' : s.student_id === current ? 'active' : 'waiting',
  }))
}
const snapshotCache = new Map<string, ProgressSnapshot>()

export function getProgress(projectId: string): ProgressSnapshot {
  const run = runs.get(projectId)
  const fixture = projectFixtures[projectId]
  const state = getProjectDemoState(projectId)
  if (!run) {
    if (state.status === 'ungraded' || !fixture) return EMPTY_SNAPSHOT
    const key = `${projectId}:${state.status}:${state.revealed.length}`
    const cached = snapshotCache.get(key)
    if (cached) return cached
    const snap: ProgressSnapshot = {
      lines: [],
      fraction: state.status === 'graded' ? 1 : 0,
      done: state.revealed.length,
      total: fixture.dashboard.students.length,
      currentStudent: null,
      stage: null,
      students: studentsProgress(projectId, state.revealed, null),
      running: false,
    }
    snapshotCache.set(key, snap)
    return snap
  }
  const key = `${projectId}:run:${run.index}:${run.lines.length}`
  const cached = snapshotCache.get(key)
  if (cached) return cached
  const snap: ProgressSnapshot = {
    lines: run.lines,
    fraction: Math.min(1, run.index / run.steps.length),
    done: state.revealed.length,
    total: fixture?.dashboard.students.length ?? 0,
    currentStudent: run.current,
    stage: run.stage,
    students: studentsProgress(projectId, state.revealed, run.stage?.studentId ?? null, run.order),
    running: run.timer != null,
  }
  snapshotCache.set(key, snap)
  return snap
}

function buildSteps(projectId: string, alreadyRevealed: Set<number>, firstStudentId?: number): Step[] {
  const fixture = projectFixtures[projectId]
  const steps: Step[] = []
  // Roster order, except that a run started from one student's page assesses
  // that student first so their disc blooms while the presenter is looking at it.
  const pending = fixture.dashboard.students
    .filter(s => !alreadyRevealed.has(s.student_id))
    .sort((a, b) => Number(b.student_id === firstStudentId) - Number(a.student_id === firstStudentId))
  steps.push({
    delay: TIMING.start,
    line: { kind: 'step', text: `${pending.length} submission${pending.length === 1 ? '' : 's'} to assess.` },
  })
  for (const student of pending) {
    const subs = fixture.submissions[String(student.student_id)] ?? []
    const files: StageFile[] = subs
      .flatMap(s => s.assets.filter(a => a.parent_asset_id == null))
      .map(a => ({ name: a.original_name ?? `${a.kind} ${a.id}`, kind: a.kind }))
    const descriptors = fixture.dashboard.matrix.filter(c => c.student_id === student.student_id && c.score != null).length
    const first = student.student_name.split(/\s+/)[0]
    const base = { studentId: student.student_id, studentName: student.student_name, files, descriptors }
    const stage = (phase: Phase, activeFile: number | null = null): StageStatus => ({ ...base, phase, activeFile })

    steps.push({
      delay: TIMING.read,
      student: student.student_name,
      stage: stage('reading', files.length ? 0 : null),
      line: { kind: 'step', text: `Reading ${first}’s work · ${files.length} file${files.length === 1 ? '' : 's'}` },
    })
    files.slice(0, MAX_FILE_LINES).forEach((f, idx) => {
      steps.push({ delay: TIMING.perFile, stage: stage('reading', idx), line: { kind: 'file', text: f.name } })
    })
    if (files.length > MAX_FILE_LINES) {
      steps.push({
        delay: TIMING.perFile,
        stage: stage('reading', MAX_FILE_LINES),
        line: { kind: 'file', text: `… and ${files.length - MAX_FILE_LINES} more` },
      })
    }
    steps.push({
      delay: TIMING.understand,
      stage: stage('understanding'),
      line: { kind: 'step', text: `AI analysis of ${first}’s work: content, reasoning, approach` },
    })
    steps.push({
      delay: TIMING.match,
      stage: stage('matching'),
      line: {
        kind: 'step',
        text: `Matched evidence to ${descriptors} skill developmental descriptor${descriptors === 1 ? '' : 's'}`,
      },
    })
    steps.push({
      delay: Math.min(TIMING.scoreCap, TIMING.scoreBase + TIMING.perDescriptor * descriptors),
      stage: stage('scoring'),
      line: { kind: 'step', text: `Scoring each descriptor against its rubric, levels 1–5…` },
    })
    steps.push({
      delay: TIMING.afterReveal,
      reveal: student.student_id,
      stage: stage('done'),
      line: { kind: 'step', text: `${first}: ${descriptors} descriptor${descriptors === 1 ? '' : 's'} scored.` },
    })
  }
  const total = fixture.grades.length
  steps.push({ delay: 0, student: null, stage: null, line: { kind: 'done', text: `Done — ${total} assessments written.` } })
  return steps
}

function tick(projectId: string) {
  const run = runs.get(projectId)
  if (!run) return
  run.timer = null
  const step = run.steps[run.index]
  if (!step) {
    setProjectDemoState(projectId, { status: 'graded', finishedAt: Date.now() })
    run.current = null
    run.stage = null
    notify(projectId)
    return
  }
  if (step.student !== undefined) run.current = step.student
  if (step.stage !== undefined) run.stage = step.stage
  if (step.line) run.lines = [...run.lines, { id: lineSeq++, ...step.line }]
  if (step.reveal != null) revealStudent(projectId, step.reveal)
  run.index += 1
  notify(projectId)
  if (run.index >= run.steps.length) {
    setProjectDemoState(projectId, { status: 'graded', finishedAt: Date.now() })
    run.current = null
    run.stage = null
    notify(projectId)
    return
  }
  run.timer = window.setTimeout(() => tick(projectId), run.steps[run.index].delay)
}

/**
 * Start the simulated assessment for a project. No-op if already running or
 * graded. `firstStudentId` moves that student to the front of the run.
 */
export function startAssessment(projectId: string, firstStudentId?: number) {
  if (!projectFixtures[projectId]) return
  const state = getProjectDemoState(projectId)
  if (runs.get(projectId)?.timer != null || state.status === 'graded') return
  setProjectDemoState(projectId, { status: 'grading', startedAt: Date.now() })
  const steps = buildSteps(projectId, new Set(state.revealed), firstStudentId)
  const order = steps.flatMap(s => (s.reveal != null ? [s.reveal] : []))
  const run: Run = { timer: null, steps, index: 0, lines: [], current: null, stage: null, order: [...state.revealed, ...order] }
  runs.set(projectId, run)
  run.timer = window.setTimeout(() => tick(projectId), steps[0].delay)
  notify(projectId)
}

/** After a reload mid-run the timers are gone; pick the run back up. */
export function resumeIfRunning(projectId: string) {
  const state = getProjectDemoState(projectId)
  if (state.status !== 'grading') return
  if (runs.get(projectId)?.timer != null) return
  runs.delete(projectId)
  setProjectDemoState(projectId, { status: 'ungraded' })
  startAssessment(projectId)
}

/** Stop any run and clear the log (used by "Reset assessment"). */
export function cancelAssessment(projectId: string) {
  const run = runs.get(projectId)
  if (run?.timer != null) window.clearTimeout(run.timer)
  runs.delete(projectId)
  notify(projectId)
}
